<template>
  <div class="report-templates">
    <el-card class="template-container">
      <!-- 搜索和操作区域 -->
      <div class="toolbar">
        <el-button type="primary" @click="createNewTemplate">
          <el-icon><Plus /></el-icon>新建模板
        </el-button>

        <div class="search-area">
          <el-input
            v-model="searchParams.keyword"
            placeholder="搜索模板名称"
            clearable
            style="width: 200px; margin-right: 10px"
            @change="handleSearch"
          />
          <el-select
            v-model="searchParams.type"
            placeholder="模板类型"
            clearable
            style="width: 120px; margin-right: 10px"
            @change="handleSearch"
          >
            <el-option label="基础模板" value="basic" />
            <el-option label="高级模板" value="advanced" />
          </el-select>
          <el-select
            v-model="searchParams.sub_department_id"
            placeholder="所属科室"
            clearable
            style="width: 150px; margin-right: 10px"
            @change="handleSearch"
          >
            <el-option label="未选择科室" :value="0" />
            <el-option
              v-for="dept in departments"
              :key="dept.id"
              :label="dept.name"
              :value="dept.id"
            />
          </el-select>
        </div>
      </div>

      <!-- 模板列表表格 -->
      <el-table
        :data="templateList"
        border
        style="width: 100%; margin-top: 20px"
      >
        <!-- <el-table-column prop="id" label="ID" width="80" align="center" /> -->
        <el-table-column prop="name" label="模板名称" />
        <el-table-column
          prop="sub_department.name"
          label="科室"
          width="120"
          align="center"
        />
        <el-table-column
          prop="template_type"
          label="模板类型"
          width="120"
          align="center"
        >
          <template #default="scope">
            <el-tag
              :type="scope.row.template_type === 'basic' ? 'primary' : 'success'"
            >
              {{
                scope.row.template_type === "basic" ? "基础模板" : "高级模板"
              }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="created_at"
          label="创建时间"
          align="center"
          width="180"
          :formatter="(row) => formatDateTime(row.created_at)"
        />
        <el-table-column
          prop="updated_at"
          label="更新时间"
          align="center"
          width="180"
          :formatter="(row) => formatDateTime(row.updated_at)"
        />
        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template #default="scope">
            <el-button size="small" @click="editTemplate(scope.row.id)"
              >编辑</el-button
            >
            <el-button size="small" @click="copyTemplate(scope.row)"
              >复制</el-button
            >
            <el-button
              size="small"
              type="danger"
              @click="deleteTemplate(scope.row.id)"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          :total="pagination.total"
          background
          layout="total, prev, pager, next"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 新建/复制模板弹窗 -->
    <el-dialog
      v-model="showCreateDialog"
      :title="copySource ? '复制模板' : '新建模板'"
      width="500px"
    >
      <el-form
        ref="createForm"
        :model="newTemplateForm"
        :rules="createRules"
        label-width="80px"
      >
        <el-form-item label="模板名称" prop="name">
          <el-input
            v-model="newTemplateForm.name"
            placeholder="请输入模板名称"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="模板类型" prop="type">
          <el-select
            v-model="newTemplateForm.type"
            placeholder="请选择模板类型"
            style="width: 100%"
          >
            <el-option label="基础模板" value="basic" />
            <el-option label="高级模板" value="advanced" />
          </el-select>
        </el-form-item>
        <el-form-item label="科室" prop="sub_department_id">
          <el-select
            v-model="newTemplateForm.sub_department_id"
            placeholder="请选择科室"
            style="width: 100%"
            clearable
          >
            <el-option
              v-for="dept in departments"
              :key="dept.id"
              :label="dept.name"
              :value="dept.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="confirmCreate"
          >确定</el-button
        >
      </template>
    </el-dialog>

    <!-- 编辑模板名称弹窗 -->
    <el-dialog v-model="showEditNameDialog" title="编辑模板名称" width="500px">
      <el-form
        ref="editNameForm"
        :model="editNameForm"
        :rules="editNameRules"
        label-width="80px"
      >
        <el-form-item label="模板名称" prop="name">
          <el-input
            v-model="editNameForm.name"
            placeholder="请输入模板名称"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditNameDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmEditName">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Plus } from "@element-plus/icons-vue";
import { useRouter } from "vue-router";
import { formatDateTime } from '@/utils/date'
import {
  createTemplate,
  deleteTemplate,
  getTemplateById,
  searchTemplates,
  updateTemplate,
} from "@/api/report-system/templete";
import { defaultCopyName, buildTemplateCopy } from "@/utils/templateCopy";
import { ElMessage } from "element-plus";
import { getSubDepartments } from "@/api/user-management/sub_department"; // 添加部门API导入

export default {
  name: "ReportTemplates",
  components: {
    Plus,
  },
  setup() {
    const router = useRouter();

    return {
      router,
    };
  },
  data() {
    return {
      // 搜索参数
      searchParams: {
        keyword: "",
        type: "",
        sub_department_id: "",
      },
      // 分页参数
      pagination: {
        current: 1,
        size: 10,
        total: 0,
      },
      // 模板列表数据
      templateList: [],
      // 部门列表
      departments: [],
      // 新建模板弹窗相关
      showCreateDialog: false,
      // 复制模板时的原模板（列表行）；为 null 表示普通新建
      copySource: null,
      // 提交中，防止连点「确定」创建出多份模板
      creating: false,
      newTemplateForm: {
        name: "",
        type: "basic",
        sub_department_id: null,
      },
      createRules: {
        name: [{ required: true, message: "请输入模板名称", trigger: "blur" }],
        type: [
          { required: true, message: "请选择模板类型", trigger: "change" },
        ],
      },
      // 编辑模板名称弹窗相关
      showEditNameDialog: false,
      editNameForm: {
        id: null,
        name: "",
      },
      editNameRules: {
        name: [{ required: true, message: "请输入模板名称", trigger: "blur" }],
      },
    };
  },
  methods: {
    formatDateTime(t) {
      return formatDateTime(t);
    },
    // 获取部门列表
    async fetchDepartments() {
      try {
        const response = await getSubDepartments();
        console.log(response)
        this.departments = response.data.rows || [];
      } catch (error) {
        ElMessage.error("获取旗县列表失败: " + (error.message || error));
      }
    },
    // 创建新模板
    createNewTemplate() {
      this.copySource = null;
      // 请求在 HTTP 层出错时拦截器返回永不结束的 Promise，finally 不会执行；打开弹窗时复位，避免按钮一直转圈
      this.creating = false;
      this.showCreateDialog = true;
      this.newTemplateForm = {
        name: "",
        type: "basic",
        sub_department_id: null,
      };
    },
    // 复制模板：沿用新建弹窗，预填原模板的名称/类型/科室
    copyTemplate(row) {
      this.copySource = row;
      this.creating = false;
      this.newTemplateForm = {
        name: defaultCopyName(row.name),
        type: row.template_type || "basic",
        sub_department_id: row.sub_department_id ?? null,
      };
      this.showCreateDialog = true;
    },
    // 按 ID 重新取原模板（拿最新内容），整份复制后创建新模板
    async createFromCopy() {
      const { data: source } = await getTemplateById(this.copySource.id);
      const copy = buildTemplateCopy(source, {
        name: this.newTemplateForm.name,
        template_type: this.newTemplateForm.type,
        sub_department_id: this.newTemplateForm.sub_department_id,
      });
      return createTemplate({ ...copy, creator_id: 1 });
    },
    // 确认创建模板
    async confirmCreate() {
      if (this.creating) return;
      this.creating = true;
      try {
        // 验证表单
        await this.$refs.createForm.validate();

        if (this.copySource) {
          const response = await this.createFromCopy();
          this.router.push({ path: `/main/report-editor/${response.data}` });
          this.showCreateDialog = false;
          return;
        }

        // 调用API创建模板
        const response = await createTemplate({
          name: this.newTemplateForm.name,
          template_type: this.newTemplateForm.type,
          creator_id: 1, // 这里应该是当前用户ID
          content: "{}", // 初始空内容
          // 必须叫 sub_department_id：models.ReportTemplate 上只有这个 json tag，
          // 之前发的 department_id 会被 ShouldBindJSON 静默丢弃，
          // 导致新建的模板科室永远为空、在科室筛选里永远查不到。
          sub_department_id: this.newTemplateForm.sub_department_id,
        });
        
        // 跳转到编辑页面
        this.router.push({
          path: `/main/report-editor/${response.data}`,
        });

        // 关闭弹窗
        this.showCreateDialog = false;
      } catch (error) {
        if (error !== "cancel") {
          console.log(error);
          // 处理表单验证错误
          if (error.name && Array.isArray(error.name)) {
            const errorMsg = error.name.map((e) => e.message).join("; ");
            ElMessage.error("创建失败: " + errorMsg);
          } else {
            ElMessage.error("创建失败: " + (error.message || error));
          }
        }
      } finally {
        this.creating = false;
      }
    },
    // 编辑模板
    editTemplate(id) {
      this.router.push({ path: `/main/report-editor/${id}` });
    },
    // 编辑模板名称
    editTemplateName(id, name) {
      this.editNameForm = {
        id: id,
        name: name,
      };
      this.showEditNameDialog = true;
    },
    // 确认编辑模板名称
    async confirmEditName() {
      try {
        // 验证表单
        await this.$refs.editNameForm.validate();

        // 调用API更新模板名称
        await updateTemplate(this.editNameForm.id, {
          name: this.editNameForm.name,
        });
        
        ElMessage.success("模板名称更新成功");
        this.showEditNameDialog = false;
        this.fetchTemplates();
      } catch (error) {
        if (error !== "cancel") {
          ElMessage.error("更新失败: " + (error.message || error));
        }
      }
    },
    // 删除模板
    async deleteTemplate(id) {
      try {
        await this.$confirm("确定要删除该模板吗?", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        });

        await deleteTemplate(id);
        ElMessage.success("删除成功");
        this.fetchTemplates();
      } catch (error) {
        if (error !== "cancel") {
          ElMessage.error("删除失败: " + (error.message || error));
        }
      }
    },
    // 搜索
    handleSearch() {
      this.pagination.current = 1;
      this.fetchTemplates();
    },
    // 获取模板列表
    async fetchTemplates() {
      try {
        const params = {
          ...this.searchParams,
          page: this.pagination.current,
          size: this.pagination.size,
        };

        const response = await searchTemplates(params);
        this.templateList = response.data.rows || []; // 获取data数组
        this.pagination.total = response.data.total || 0; // 更新总条数
      } catch (error) {
        ElMessage.error("获取模板列表失败: " + (error.message || error));
      }
    },
    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.size = size;
      this.fetchTemplates();
    },
    // 当前页改变
    handleCurrentChange(current) {
      this.pagination.current = current;
      this.fetchTemplates();
    },
  },
  mounted() {
    this.fetchTemplates();
    this.fetchDepartments();
  },
};
</script>

<style scoped>
.report-templates {
  padding: 20px;
}

.template-container {
  max-width: 1200px;
  margin: 0 auto;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-area {
  display: flex;
  align-items: center;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
